import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { applyButtonTheme } from "../utils/themeUtils"
import { buildPublicPageUrl, buildServiceUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const ServicesSectionMobile = ({ section, practice, theme, highlightedField, pageSlug }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "list"
  const showCTA = settings.showCTA || true
  const selectedServiceIds = settings.selectedServiceIds || []
  const showBackgroundColor = settings.showBackgroundColor || false
  const backgroundColor = settings.backgroundColor || "#ffffff"

  const handleServiceClick = (serviceId) => {
    window.location.href = buildServiceUrl(serviceId, practice)
  }

  // Get practice services
  const allPracticeServices = practice?.services || []

  // If specific services are selected, use those; otherwise use all services
  const selectedServices =
    selectedServiceIds.length > 0
      ? allPracticeServices.filter((service) => selectedServiceIds.includes(service.id))
      : allPracticeServices

  // Combine services, limit based on layout and page
  const isServicesPage = pageSlug === "services"
  const maxItems = isServicesPage
    ? 999 // Show all items on services page
    : layout === "highlights_2"
    ? 2
    : layout === "highlights_3"
    ? 3
    : 3 // Limit to 3 for list layout when not on services page
  const allItems = selectedServices.slice(0, maxItems)
  const hasMoreServices = selectedServices.length > maxItems

  const formatPrice = (amountCents, currency) => {
    if (amountCents === 0) {
      return "Free"
    }
    const amount = amountCents / 100
    return `$${amount.toFixed(2)}${currency === "CAD" ? " CAD" : ""}`
  }

  // Render service card for mobile (vertical stack with image on top)
  const renderMobileServiceCard = (item, index) => {
    const price = item.amountCents ? formatPrice(item.amountCents, practice?.defaultCurrency || "USD") : ""
    const duration = item.timeLength ? `${item.timeLength} min` : ""

    // Format location and service type information
    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    // Get image URL from various possible sources
    const imageUrl =
      item.imageUrl ||
      item.mediumWebpUrl ||
      (item.photoHandles?.large?.webp ? `https://cdn.filestackcontent.com/${item.photoHandles.large.webp}` : null) ||
      (item.photoHandles?.original?.any ? `https://cdn.filestackcontent.com/${item.photoHandles.original.any}` : null)

    // Truncate description for display
    const displayDescription =
      item.description && item.description.length > 200 ? item.description.substring(0, 200) + "..." : item.description

    return (
      <div key={index} className="flex w-full flex-col items-start justify-start gap-4">
        {/* Image - Full width with aspect ratio */}
        <div className="aspect-[396/250] w-full overflow-hidden bg-gray-100">
          <img
            src={imageUrl || placeholderImage}
            alt={item.name}
            className="h-full w-full object-cover"
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
        </div>

        {/* Content */}
        <div className="flex w-full flex-col items-start justify-start gap-2 overflow-hidden">
          {/* Price and Title */}
          <div className="flex w-full flex-col items-start justify-start">
            {price && (
              <div
                className="w-full truncate text-[18px] font-semibold leading-[24px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                  color: theme?.colors?.textDark || "#55433F"
                }}>
                {price}
              </div>
            )}
            <div
              className="line-clamp-2 w-full text-[22px] font-bold leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: theme?.colors?.textDark || "#454A4F"
              }}>
              {item.name}
            </div>
          </div>

          {/* Duration and Location */}
          {(duration || locationInfo) && (
            <div
              className="w-full truncate text-[14px] font-normal leading-[20px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.textDark || "#55433F"
              }}>
              {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
            </div>
          )}

          {/* Description */}
          {displayDescription && (
            <div
              className="prose prose-lg line-clamp-3 w-full max-w-none text-[16px] font-normal leading-[24px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.textDark || "#454A4F"
              }}
              dangerouslySetInnerHTML={{ __html: displayDescription }}></div>
          )}

          {/* Details button */}
          <div
            className="flex w-full cursor-pointer items-center justify-center gap-3 px-12 py-6 transition-opacity hover:opacity-90"
            style={{
              backgroundColor: theme?.colors?.primary || "#BD654F",
              borderRadius: theme?.borderRadius || "12px"
            }}
            onClick={() => handleServiceClick(item.id)}>
            <div
              className="text-[16px] font-normal leading-[24px] text-white"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.bgLight || "#F4ECE9"
              }}>
              Details
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <section
      className={twMerge("relative py-8 pb-[72px]", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: showBackgroundColor ? backgroundColor : theme?.colors?.background || "#FFFFFF"
      }}>
      <AnimatedSection className="mx-auto max-w-[1160px] px-5">
        <div className="flex w-full flex-col items-start gap-16">
          {/* Header */}
          <div className="flex w-full flex-col items-start gap-8">
            {/* Title and Browse Services Button */}
            <div className="flex w-full flex-col items-start gap-6">
              <div className="flex w-full items-center justify-between gap-4">
                <h2
                  className="text-left text-[27px] font-bold leading-[32px]"
                  style={{
                    fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                    fontWeight: theme?.fonts?.headingWeight || "700",
                    color: theme?.colors?.textDark || "#55433F"
                  }}>
                  {section.headline || "Services"}
                </h2>

                {/* Browse Services Button - Only show if not on services page */}
                {pageSlug !== "services" && (
                  <button
                    onClick={() => (window.location.href = buildPublicPageUrl("services", practice))}
                    className="flex items-center justify-center gap-3 px-8 py-2 transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: theme?.colors?.primary || "#BD654F",
                      borderRadius: theme?.borderRadius || "12px",
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                      color: theme?.colors?.bgLight || "#F4ECE9",
                      fontSize: "14px",
                      lineHeight: "20px"
                    }}>
                    Browse Services
                  </button>
                )}
              </div>

              {section.body && (
                <p
                  className="max-w-3xl text-left text-[18px] leading-[24px]"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                    color: theme?.colors?.textDark || "#55433F"
                  }}>
                  {section.body}
                </p>
              )}
            </div>
          </div>

          {/* Services Grid */}
          {allItems.length > 0 && (
            <div className="w-full">
              {layout === "highlights_2" || layout === "highlights_3" ? (
                // Highlights layout - grid for mobile
                <div
                  className={`grid gap-6 ${layout === "highlights_2" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                  {allItems.map((item, index) => renderMobileServiceCard(item, index))}
                </div>
              ) : (
                // List layout - vertical stack
                <div className="flex flex-col gap-10">
                  {allItems.map((item, index) => renderMobileServiceCard(item, index))}
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {allItems.length === 0 && (
            <div className="flex flex-col items-center gap-6 py-16 text-center">
              <div className="text-6xl">🔧</div>
              <h3
                className="text-2xl font-semibold"
                style={{
                  fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                  color: theme?.colors?.textDark || "#55433F"
                }}>
                No services available
              </h3>
              <p
                className="text-lg text-gray-600"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                }}>
                {practice?.services?.length === 0
                  ? "This practice hasn't added any services yet."
                  : "No services match the current selection."}
              </p>
            </div>
          )}

          {/* Show More Button - Only show if no CTA button is shown and there are more services */}
          {!showCTA && hasMoreServices && !isServicesPage && (
            <div className="mt-8 text-center">
              <button
                onClick={() => (window.location.href = buildPublicPageUrl("services", practice))}
                className="rounded-lg px-8 py-3 font-medium text-white transition-colors"
                style={applyButtonTheme(
                  {
                    backgroundColor: theme?.colors?.primary || "#BD654F",
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    borderRadius: theme?.borderRadius || "12px"
                  },
                  theme
                )}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                  e.target.style.transform = "translateY(-2px)"
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                  e.target.style.transform = "translateY(0)"
                }}>
                View All Services
              </button>
            </div>
          )}
        </div>
      </AnimatedSection>
    </section>
  )
}

export default ServicesSectionMobile
